/**
 * @file MenuCategories.js
 * @author Stanley Chen
 * @description Component that displays featured menu categories on the homepage
 * with images, descriptions, and links to specific sections of the full menu
 */

import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import './MenuCategories.css';

const menuCategories = [
    {
        id: 1,
        title: 'Swan Lake',
        description: 'Enjoy a classic appetizer featuring crispy duck skin rolled with fresh green onion and cucumber. Simple, bold flavors with a satisfying crunch in every bite.',
        image: '/images/swan-lake.jpg',
        tag: 'Chef Recommended',
        buttonText: 'View more appetizers',
        menu: '/menu',
        type: 'kitchen'
    },
    {
        id: 2,
        title: 'Hibachi Combination',
        description: 'Choose 2 or more of your favorite proteins to combine into a sumptious meal. Served with our own hibachi sauces and your choice of fried rice, white rice, or noodles.',
        image: '/images/gallery/gallery-6.jpg',
        tag: 'Best Value',
        buttonText: 'View more hibachi',
        menu: '/menu',
        type: 'hibachi'
    },
    {
        id: 3,
        title: 'Sashimi Dinner',
        description: '',
        image: '/images/menu/sashimi-dinner.jpg',
        tag: 'Perfect For 2',
        buttonText: 'View more Sashimi',
        menu: '/menu',
        type: 'sushi'

    },
    {
        id: 4,
        title: 'Basil Filet Mignon',
        description: '',
        image: '/images/menu/basil-filet-mignon.jpg',
        tag: 'Customer Favorite',
        buttonText: 'View more Kitchen Dinners',
        menu: '/menu',
        type: 'sushi'
    },
    {
        id: 5,
        title: 'Crispy Calamari',
        description: '',
        image: '/images/menu/crispy-calamari.jpg',
        tag: 'Value Choice',
        buttonText: 'View more Appetizers',
        menu: '/menu',
        type: 'kitchen'
    },
    {
        id: 6,
        title: 'Gekkeikan Nigori Sake',
        description: '',
        image: '/images/menu/sake3.jpg',
        tag: 'Bar Favorite',
        buttonText: 'View more drinks',
        menu: '/drinks',
        type: 'sake'
    },
];

const MenuCategories = () => {
    const [expanded, setExpanded] = useState(false);
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, amount: 0.2 });

    // Get the initial categories (first 3)
    const initialCategories = menuCategories.slice(0, 3);

    // Get the extra categories (from 4th onwards)
    const extraCategories = menuCategories.slice(3);

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.05
            }
        }
    };

    const item = {
        hidden: { opacity: 0, scale: 0.95, y: 20 },
        show: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 15,
                duration: 0.3
            }
        }
    };

    return (
        <section className="menu-categories-section" id="menu-categories" ref={ref}>
            <div className="menu-categories-container">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="menu-categories-title"
                >
                    MENU HIGHLIGHTS
                </motion.h2>

                <motion.div
                    className="menu-categories-grid"
                    variants={container}
                    initial="hidden"
                    animate={isInView ? "show" : "hidden"}
                >
                    {initialCategories.map((category) => (
                        <motion.div
                            key={category.id}
                            className="menu-category-item"
                            variants={item}
                        >
                            <div className="menu-category-image-container">
                                <img
                                    src={category.image}
                                    alt={category.title}
                                    className="menu-category-image"
                                />
                            </div>
                            <div className="menu-category-content">
                                <div className="menu-category-tag">{category.tag}</div>
                                <h3 className="menu-category-title">{category.title}</h3>
                                {/*<p className="menu-category-description">{category.description}</p>*/}
                                <Link to={category.menu} state={{ activeCategory: category.type }}>
                                    <button className="menu-category-button">
                                        {category.buttonText}
                                    </button>
                                </Link>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                <AnimatePresence>
                    {expanded && (
                        <motion.div
                            className="menu-categories-grid menu-categories-expanded"
                            variants={container}
                            initial="hidden"
                            animate="show"
                            exit={{
                                opacity: 0,
                                transition: {
                                    staggerChildren: 0.05,
                                    staggerDirection: -1,
                                    when: "afterChildren"
                                }
                            }}
                        >
                            {extraCategories.map((category) => (
                                <motion.div
                                    key={category.id}
                                    className="menu-category-item"
                                    variants={item}
                                >
                                    <div className="menu-category-image-container">
                                        <img
                                            src={category.image}
                                            alt={category.title}
                                            className="menu-category-image"
                                        />
                                    </div>
                                    <div className="menu-category-content">
                                        <div className="menu-category-tag">{category.tag}</div>
                                        <h3 className="menu-category-title">{category.title}</h3>
                                        {/*<p className="menu-category-description">{category.description}</p>*/}
                                        <Link to={category.menu} state={{ activeCategory: category.type }}>
                                            <button className="menu-category-button">
                                                {category.buttonText}
                                            </button>
                                        </Link>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>

                <motion.div
                    className="view-more-container"
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    <button
                        className="view-more-button"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? "Show less" : "Show more"}
                    </button>
                </motion.div>
                <div className="line"></div>
            </div>
            
        </section>
    );
};

export default MenuCategories;